import apiClient from './api-client.js';
import { getApiToken } from './tokenUtils.js';
import { getJwtToken } from './jwtTokenManager.js';
import { safeStorageGet, safeStorageRemove, safeStorageSet } from './safe-storage.js';
import { identifyDevice } from './device-identify.js';

const userId = () => String(document.querySelector('meta[name="user-id"]')?.content || '');
const identity = () => `${userId()}:${getJwtToken()?.account?.userId || ''}`;
const REPORTED_KEY = 'timezone-sync-reported';
// Bounds how long a report from another device can hold the server away from this device zone.
const REPORT_TTL_MS = 24 * 60 * 60 * 1000;
const loadedAt = Date.now();
let revision = 0;
let queued = false;
let pendingUpdates = 0;
let requests = Promise.resolve();

function storedReport(expectedUserId, timezone) {
    try {
        const last = JSON.parse(safeStorageGet(REPORTED_KEY) || 'null');
        const age = Date.now() - last?.at;
        const fresh = last?.userId === expectedUserId && last.timezone === timezone && age >= 0 && age < REPORT_TTL_MS;
        const state = last?.state;
        return fresh && ['automatic', 'manual'].includes(state?.timezone_mode) && typeof state.tz === 'string' ? last : null;
    } catch {
        return null;
    }
}

function rememberState(expectedUserId, timezone, state, at) {
    safeStorageSet(REPORTED_KEY, JSON.stringify({
        userId: expectedUserId, timezone, at, state: { timezone_mode: state.timezone_mode, tz: state.tz }
    }));
}

// A unique marker makes the compare-and-set in report() fail for a request that overlaps an explicit update.
function invalidateReport() {
    const marker = JSON.stringify({ cleared: `${Date.now()}:${Math.random()}` });
    if (safeStorageSet(REPORTED_KEY, marker)) return marker;
    safeStorageRemove(REPORTED_KEY);
    return null;
}

function reuseStoredReport(expectedUserId, timezone) {
    const last = storedReport(expectedUserId, timezone);
    // A greeting reset by a token change needs the authoritative state from a report.
    if (!last || document.querySelector('[data-timezone-greeting]')?.dataset.timezoneMode === '') return false;
    // Server-rendered state is fresher than a pair stored before this document loaded.
    if (last.at > loadedAt && !pendingUpdates && String(getJwtToken()?.account?.userId || '') === userId()) {
        publish(last.state);
    }
    return true;
}

function deviceTimezone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || null;
    } catch {
        return null;
    }
}

function publish(state) {
    const timezone = state.timezone_mode === 'automatic' ? deviceTimezone() || state.tz : state.tz;
    refreshGreeting(timezone, state);
    window.dispatchEvent(new CustomEvent('timezone-change', { detail: { ...state, timezone } }));
}

function writeCookie(timezone) {
    document.cookie = `device_timezone=${encodeURIComponent(timezone)}; Path=/; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}`;
}

async function report(expectedUserId, timezone, generation) {
    if (expectedUserId !== userId() || generation !== revision || !await getApiToken()) return;
    if (expectedUserId !== userId() || generation !== revision) return;
    // Another tab or an earlier activation can report the same pair while this one waits.
    if (reuseStoredReport(expectedUserId, timezone)) return;
    const stored = safeStorageGet(REPORTED_KEY);
    const sentAt = Date.now();
    const startedIdentity = identity();
    const response = await apiClient.request('/timezone-sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ timezone, expected_user_id: expectedUserId }).toString()
    });
    if (!response.ok) return;
    const state = await response.json();
    // A pair written meanwhile by an explicit update in another tab is newer than this response.
    if (safeStorageGet(REPORTED_KEY) === stored) rememberState(expectedUserId, timezone, state, sentAt);
    if (startedIdentity === identity() && expectedUserId === userId() && generation === revision) publish(state);
}

function reconcile() {
    const timezone = deviceTimezone();
    if (timezone) writeCookie(timezone);
    refreshGreeting(timezone);
    void identifyDevice();
    if (!timezone || !userId() || queued) return;
    if (reuseStoredReport(userId(), timezone)) return;
    queued = true;
    const expectedUserId = userId();
    const generation = revision;
    // Initial load and its pageshow share one activation.
    queueMicrotask(() => {
        queued = false;
        requests = requests.catch(() => {}).then(() => report(expectedUserId, timezone, generation)).catch(() => {});
    });
}

function update(mode, timezone) {
    const expectedUserId = userId();
    const generation = ++revision;
    // A response fence cannot undo an older report that commits after an explicit update.
    pendingUpdates++;
    const operation = requests.catch(() => {})
        .then(() => writePreference(expectedUserId, generation, mode, timezone))
        .finally(() => { pendingUpdates--; });
    requests = operation.catch(() => {});
    return operation;
}

async function writePreference(expectedUserId, generation, mode, timezone) {
    if (!expectedUserId || !await getApiToken()) throw new Error('Timezone authentication failed');
    if (generation !== revision || expectedUserId !== userId()
        || String(getJwtToken()?.account?.userId || '') !== expectedUserId) {
        throw new Error('Timezone account changed');
    }
    const marker = invalidateReport();
    const startedIdentity = identity();
    const zone = deviceTimezone();
    const sentAt = Date.now();
    let state;
    try {
        const response = await apiClient.request(`/settings/${encodeURIComponent(expectedUserId)}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ timezone_mode: mode, tz: mode === 'automatic' ? zone || timezone : timezone })
        });
        if (!response.ok) throw new Error('Timezone update failed');
        state = await response.json();
    } catch (error) {
        // The server can hold this update even when the response is lost.
        invalidateReport();
        throw error;
    }
    // Another tab wrote during this request and the commit order is unknown, so the next activation reports.
    if (zone && safeStorageGet(REPORTED_KEY) === marker) rememberState(expectedUserId, zone, state, sentAt);
    else invalidateReport();
    if (generation !== revision || expectedUserId !== userId() || startedIdentity !== identity()) {
        throw new Error('Timezone account changed');
    }
    publish(state);
    return state;
}

window.TimezonePreference = { reconcile, update };
window.addEventListener('storage', event => {
    if (event.key === 'redux-state') {
        revision++;
        const heading = document.querySelector('[data-timezone-greeting]');
        if (heading) heading.dataset.timezoneMode = '';
    }
});
window.addEventListener('pageshow', reconcile);
document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') reconcile();
});
function refreshGreeting(deviceZone, state = null) {
    const heading = document.querySelector('[data-timezone-greeting]');
    if (!heading) return;
    const tokenUserId = String(getJwtToken()?.account?.userId || '');
    if (!userId() || heading.dataset.userId !== userId() || (tokenUserId && tokenUserId !== userId())) {
        heading.dataset.timezoneMode = '';
        return;
    }
    if (state) {
        heading.dataset.timezoneMode = state.timezone_mode;
        heading.dataset.timezone = state.tz;
    }
    const mode = heading.dataset.timezoneMode;
    if (mode !== 'automatic' && mode !== 'manual') return;
    const timezone = mode === 'automatic' ? deviceZone || heading.dataset.timezone : heading.dataset.timezone;
    try {
        const hour = Number(new Intl.DateTimeFormat('en-US', {
            hour: 'numeric', hourCycle: 'h23', timeZone: timezone || 'UTC'
        }).format(new Date()));
        const part = hour >= 5 && hour < 12 ? 'morning' : hour >= 12 && hour < 18 ? 'afternoon' : 'evening';
        heading.textContent = `Good ${part}, ${heading.dataset.username}.`;
    } catch {
        // A later valid report can refresh the greeting.
    }
}
// Deferred bundles normally precede pageshow; late bundles need their own activation.
if (document.readyState === 'complete') reconcile();
else {
    const timezone = deviceTimezone();
    if (timezone) writeCookie(timezone);
    refreshGreeting(timezone);
}
